// src/components/Calendar.js
// Componente de calendario para agendamiento de citas

import React, { useState } from 'react';
import { Card, Row, Col, Button, Badge, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import './CalendarModern.css';

const Calendar = ({ 
  year, 
  month, 
  appointments = [], 
  onDateClick, 
  onMonthChange, 
  onYearChange 
}) => {
  const navigate = useNavigate();
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedDateStr, setSelectedDateStr] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  // Obtener el primer día del mes y cuántos días tiene
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay(); // 0 = domingo, 1 = lunes, etc.

  // Obtener fecha actual
  const today = new Date();
  const currentDay = today.getDate();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();
  const todayStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(currentDay).padStart(2, '0')}`;

  // Nombres de los días de la semana
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  
  // Nombres de los meses
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Generar años para el selector (últimos 5 años y próximos 5 años)
  const years = [];
  for (let i = currentYear - 5; i <= currentYear + 5; i++) {
    years.push(i);
  }
  
  // Verificar si una fecha es hoy
  const isToday = (dateStr) => {
    return dateStr === todayStr;
  };

  // Generar días del calendario
  const calendarDays = [];
  
  // Días vacíos al inicio del mes
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }
  
  // Días del mes
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayAppointments = appointments.filter(apt => 
      apt.start_at && apt.start_at.startsWith(dateStr)
    );
    
    calendarDays.push({
      day,
      dateStr,
      appointments: dayAppointments
    });
  }

  // Obtener citas para un día específico
  const getAppointmentsForDay = (dateStr) => {
    return appointments.filter(apt => 
      apt.start_at && apt.start_at.startsWith(dateStr)
    );
  };

  // Verificar si un día tiene citas
  const hasAppointments = (dateStr) => {
    return getAppointmentsForDay(dateStr).length > 0;
  };

  // Obtener el número de citas para un día
  const getAppointmentCount = (dateStr) => {
    return getAppointmentsForDay(dateStr).length;
  };

  // Manejar clic en día del calendario
  const handleDayClick = (dateStr, day) => {
    setSelectedDateStr(dateStr);
    setSelectedDay(day);
    setShowActionModal(true);
  };

  // Cerrar modal de acciones
  const handleCloseActionModal = () => {
    setShowActionModal(false);
    setSelectedDateStr(null);
    setSelectedDay(null);
  };

  // Manejar acción de agendar cita
  const handleScheduleAppointment = () => {
    if (selectedDateStr && onDateClick) {
      onDateClick(selectedDateStr, selectedDay);
    }
    handleCloseActionModal();
  };

  // Manejar acción de ver gestión de citas
  const handleViewAppointments = () => {
    if (selectedDateStr) {
      navigate(`/gestion-citas?date=${selectedDateStr}`);
    }
    handleCloseActionModal();
  };

  // Formatear fecha para mostrar
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    const date = new Date(year, month - 1, day);
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return date.toLocaleDateString('es-ES', options);
  };

  return (
    <Card className="modern-calendar-card shadow-sm">
      <Card.Header className="bg-gradient-primary text-white">
        <Row className="align-items-center">
          <Col md={4}>
            <h5 className="mb-0 fw-bold">
              <i className="fas fa-calendar-alt me-2"></i>
              Calendario de Citas
            </h5>
          </Col>
          <Col md={4}>
            <div className="d-flex align-items-center gap-2">
              <select 
                className="form-select form-select-sm shadow-sm" 
                value={month} 
                onChange={(e) => onMonthChange(parseInt(e.target.value))}
              >
                {monthNames.map((monthName, index) => (
                  <option key={index} value={index + 1}>
                    {monthName}
                  </option>
                ))}
              </select>
              <select 
                className="form-select form-select-sm shadow-sm" 
                value={year} 
                onChange={(e) => onYearChange(parseInt(e.target.value))}
              >
                {years.map(yearOption => (
                  <option key={yearOption} value={yearOption}>
                    {yearOption}
                  </option>
                ))}
              </select>
            </div>
          </Col>
          <Col md={4} className="text-end">
            <small className="opacity-75">
              <i className="fas fa-hand-pointer me-1"></i>
              Haz clic en un día para agendar
            </small>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body className="p-4">
        {/* Encabezados de días de la semana */}
        <Row className="mb-3 g-0">
          {dayNames.map((dayName, index) => (
            <Col 
              key={index} 
              className="text-center fw-bold text-uppercase day-header"
              style={{ 
                color: index === 0 || index === 6 ? '#dc3545' : '#6c757d',
                fontSize: '0.85rem',
                letterSpacing: '0.5px'
              }}
            >
              {dayName}
            </Col>
          ))}
        </Row>

        {/* Días del calendario */}
        <div className="modern-calendar-grid">
          {Array.from({ length: Math.ceil(calendarDays.length / 7) }, (_, weekIndex) => (
            <Row key={weekIndex} className="mb-2 g-2">
              {Array.from({ length: 7 }, (_, dayIndex) => {
                const dayData = calendarDays[weekIndex * 7 + dayIndex];
                
                if (!dayData) {
                  return (
                    <Col key={dayIndex} className="calendar-day-col">
                      <div className="calendar-day-empty"></div>
                    </Col>
                  );
                }

                const { day, dateStr, appointments: dayAppointments } = dayData;
                const appointmentCount = dayAppointments.length;
                const hasAppts = appointmentCount > 0;
                const isTodayDate = isToday(dateStr);

                return (
                  <Col key={dayIndex} className="calendar-day-col">
                    <div
                      className={`calendar-day-modern ${hasAppts ? 'has-appointments' : 'available'} ${
                        isTodayDate ? 'today-highlight' : ''
                      }`}
                      onClick={() => handleDayClick(dateStr, day)}
                      role="button"
                      tabIndex={0}
                    >
                      {isTodayDate && (
                        <div className="today-pulse"></div>
                      )}
                      
                      <div className="day-number">
                        {day}
                      </div>
                      
                      {isTodayDate && (
                        <div className="today-label">Hoy</div>
                      )}
                      
                      {hasAppts && (
                        <>
                          <Badge 
                            bg="primary" 
                            className="appointment-badge"
                            pill
                          >
                            {appointmentCount}
                          </Badge>
                          <div className="appointment-indicator">
                            <i className="fas fa-paw me-1"></i>
                            {appointmentCount} cita{appointmentCount !== 1 ? 's' : ''}
                          </div>
                        </>
                      )}
                      
                      {!hasAppts && !isTodayDate && (
                        <div className="available-label">Disponible</div>
                      )}
                    </div>
                  </Col>
                );
              })}
            </Row>
          ))}
        </div>

        {/* Leyenda mejorada */}
        <Row className="mt-4 pt-3 border-top">
          <Col>
            <div className="d-flex align-items-center justify-content-center gap-4 flex-wrap">
              <div className="legend-item">
                <div className="legend-box today-box"></div>
                <small className="text-muted ms-2">Día Actual</small>
              </div>
              <div className="legend-item">
                <div className="legend-box appointments-box"></div>
                <small className="text-muted ms-2">Con Citas</small>
              </div>
              <div className="legend-item">
                <div className="legend-box available-box"></div>
                <small className="text-muted ms-2">Disponibles</small>
              </div>
            </div>
          </Col>
        </Row>
      </Card.Body>

      {/* Modal de acciones para el día seleccionado */}
      <Modal show={showActionModal} onHide={handleCloseActionModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-calendar-day me-2"></i>
            {selectedDateStr && formatDateForDisplay(selectedDateStr)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-grid gap-2">
            <Button
              variant="primary"
              size="lg"
              onClick={handleScheduleAppointment}
              className="d-flex align-items-center justify-content-center"
            >
              <i className="fas fa-plus-circle me-2"></i>
              Agendar Cita
            </Button>
            <Button
              variant="outline-primary"
              size="lg"
              onClick={handleViewAppointments}
              className="d-flex align-items-center justify-content-center"
            >
              <i className="fas fa-list me-2"></i>
              Ver Gestión de Citas
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseActionModal}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default Calendar;
